import axios from "axios";
import { setupCache } from "axios-cache-interceptor";
import store from "../redux-store/store";
import { logoutUser } from "redux-store/auth/actions";
import { handleUnAuthorizedAction } from "redux-store/auth/reducers";
import { getActingOrgId, isSuperAdmin } from "utils/abacRoles";
import { NON_TENANT_SUBDOMAINS } from "config/tenancy";

function getSubdomain() {
  if (typeof window === "undefined") {
    return null;
  }

  const hostname = window.location.hostname;
  const parts = hostname.split(".");
  if (parts.length > 2) {
    return parts[0];
  }
  return null;
}

// Non-tenant host handling lives in config/tenancy — see NON_TENANT_SUBDOMAINS
// there for why "energy" must never be sent as the tenant header.
export { NON_TENANT_SUBDOMAINS } from "config/tenancy";

const baseURL = process.env.REACT_APP_API_URL;
const headers = {
  "Content-Type": "application/json",
};
export const requestInterceptors = [
  (config) => {
    if (localStorage.authToken) config.headers.Authorization = `Bearer ${localStorage.authToken}`;
    const hostSubdomain = getSubdomain();
    const subdomain =
      localStorage._subdomain && (!hostSubdomain || NON_TENANT_SUBDOMAINS.has(hostSubdomain))
        ? localStorage._subdomain
        : NON_TENANT_SUBDOMAINS.has(hostSubdomain)
        ? null
        : hostSubdomain;
    if (subdomain) config.headers._subdomain = `${subdomain}`;

    // Add organization ID for defense-in-depth tenant isolation
    const authState = store.getState()?.auth;
    const orgId = authState?.user?.organizationId ?? authState?.user?.userDetails?.organizationId;
    if (orgId) {
      config.headers['X-Organization-Id'] = orgId.toString();
    }

    // Super-admin tenant switcher: only attach X-Acting-Org-Id when the
    // current user is a super admin AND has selected an org in the
    // platform-ABAC TenantSwitcher. Backend ActingOrgMiddleware will
    // silently ignore the header for non-platform-admin subjects, but
    // we still gate on the frontend to avoid sending a useless header
    // (and to keep regular tenant requests free of the extra metadata).
    const actingOrgId = getActingOrgId();
    if (actingOrgId && isSuperAdmin(authState?.user)) {
      config.headers["X-Acting-Org-Id"] = actingOrgId.toString();
    }

    return config;
  },
  (error) => Promise.reject(error),
];
export const responseInterceptors = [
  (result) => {
    return result;
  },
  (error) => {
    if (error?.response?.status === 401) {
      localStorage?.clear();
      store.dispatch(logoutUser());
      store.dispatch(handleUnAuthorizedAction(true));
    } else if (error?.response?.status === 403) {
      console.warn(
        `[API] 403 Forbidden: ${error?.config?.method?.toUpperCase()} ${error?.config?.url}`,
        error?.response?.data?.message || ""
      );
    } else if (error.code === "ERR_CANCELED") {
      return Promise.resolve({ status: 499 });
    }
    return Promise.reject(error);
  },
];

const apiInstance = axios.create({
  baseURL,
  headers,
});

apiInstance.interceptors.request.use(...requestInterceptors);

apiInstance.interceptors.response.use(...responseInterceptors);

export const apiInstanceFileUpload = axios.create({
  baseURL,
  headers: {
    "Content-Type": "multipart/form-data",
  },
});

apiInstanceFileUpload.interceptors.request.use(...requestInterceptors);

apiInstanceFileUpload.interceptors.response.use(...responseInterceptors);

const cachedApi = axios.create({
  baseURL,
  headers,
});

cachedApi.interceptors.request.use(...requestInterceptors);

export const cachedApiInstance = setupCache(cachedApi);

cachedApi.interceptors.response.use(...responseInterceptors);

export default apiInstance;
